/**
 * Scripted shouts.
 *
 * Time-based lines are pure functions of the run's `simulationTime`, exactly
 * like the stumble schedule: the same run always hears the same thing at the
 * same moment. Hannu's lines are event-driven instead (he shouts when he climbs
 * out and on every swipe), but still deterministic - see world/Hannu.ts.
 *
 * Every line is both printed on the HUD and spoken out loud by the browser's
 * speech synthesis (src/audio/Voice.ts), so the text here is the script for an
 * actor, not a subtitle: write it the way somebody would actually yell it.
 */

import type { OneShotSound } from '../audio/AudioManager';
import type { VoiceStyle } from './audio';

export interface ScriptedLine {
  id: string;
  text: string;
  /** Simulation time the line appears. */
  startTime: number;
  duration: number;
  /**
   * Shouted from somewhere across the yard: rendered smaller and dimmer, so it
   * reads as distance rather than as a UI notification. Speech has no 3D
   * position, so `voice.volume` is what sells the distance in the ear.
   */
  distant?: boolean;
  /** How it is delivered. Anything omitted falls back to `VOICE.base`. */
  voice?: VoiceStyle;
  /** Wordless stand-in on machines with no speech synthesis installed. */
  cue?: OneShotSound;
}

/** Pre-run wake-up clock. Somebody wants you at your PC before the timer starts. */
export const RUN_START_LINES: ScriptedLine[] = [
  {
    id: 'finaali',
    text: '"Kimmo" nyt vauhtia – kaikki oottaa sua!',
    startTime: 0.35,
    duration: 3.6,
    distant: true,
    // Across the yard and already annoyed: quick, a little shrill, not loud.
    voice: { rate: 1.18, pitch: 1.08, volume: 0.75 },
    cue: 'shout-finaali',
  },
];

/**
 * Hannu's shouting is event-driven rather than scheduled, so his line has no
 * entry in a list - but it is still a line somebody can record, and it needs
 * the same stable id everywhere: `world/Hannu.ts` fires it, `Game` speaks it,
 * and `src/audio/samples/voice/hannu.mp3` would take it over.
 */
export const HANNU_LINE_ID = 'hannu';

/**
 * A line somebody shouts because of where the player *is* rather than because
 * of what the clock says.
 *
 * Deterministic all the same, and for a better reason than the scheduled lines
 * are: the same inputs put the runner on the same square metre on the same
 * step, so a replay hears this on the step the run did. What it is not is
 * anything the simulation has to know about - a shout moves nobody - so it is
 * triggered in `Game` off the player's position, the same way the yell after
 * the window goes in is.
 *
 * *Where* it is shouted from is deliberately not in here, and neither is the
 * shape of the place that sets it off. That is level data, and a second copy
 * of it in the script is a copy that goes stale the first time somebody slides
 * the thing it belongs to along a wall.
 */
export interface SpokenLine {
  id: string;
  text: string;
  duration: number;
  /** How it is delivered. Anything omitted falls back to `VOICE.base`. */
  voice?: VoiceStyle;
  /** Wordless stand-in on machines with no speech synthesis installed. */
  cue?: OneShotSound;
}

/** One that goes off inside a circle: near enough to the man, and said. */
export interface ProximityLine extends SpokenLine {
  /** How close the player has to come, in metres, to be seen. */
  radius: number;
}

/**
 * The cook at the muurikka, as Kimmo approaches the navetta entrance.
 *
 * He is the one man on the route who wants nothing from the runner: the yell
 * out of the pirtti is somebody who wants him at a PC, Hannu wants a klapi,
 * and this one is offering him breakfast. It is also the only line in the game
 * that can be safely ignored, which is the joke - there is a clock running.
 */
export const KOKKI_LINE: ProximityLine = {
  id: 'kokki',
  text: 'Onks Kimmo kaikki hyvin? Ota tosta vaan – ne on kaikille!',
  // Presentation only: proximity on the porch's right side, gated in Game.
  radius: 9,
  duration: 4.1,
  // Close, loud and pleased about it. He is the only person in the venue this
  // morning who is having a nice time, and the delivery is the whole of it.
  voice: { rate: 1.02, pitch: 0.92, volume: 0.95 },
  cue: 'shout-kokki',
};

/**
 * Mäysä in the porstua, the moment Kimmo comes up the porch expecting to run
 * straight through it.
 *
 * This one is not colour, unlike every other line in the game: it is the *only*
 * thing that explains why a man is standing in a doorway that used to be a
 * doorway. Kimmo hits an invisible wall with a person in front of it, and the
 * person points down the facade - the arm says where and this says why. Take
 * it out and the mechanic has to be discovered by walking into it twice.
 *
 * Which is why it is not a proximity line. A line that explains a wall is
 * noise until the wall has been walked into, and a circle round the man goes
 * off long before that: five metres of it is most of the way back to the
 * veräjä on one side and the paving outside the porch's east wall on the
 * other, which is where everybody heading for the pan runs. The shout would
 * land on a runner who had not tried the door at all.
 *
 * So the trigger is the doorway itself, and `Game` waits for the runner to be
 * standing in it - between the porch walls and over the man asleep across its
 * mouth, who is the reason getting in there is a hop rather than a step.
 * Clearing him *is* the attempt, and this is the answer to it. The geometry is
 * read out of the level rather than typed out here; see `MAYSA_PORCH`.
 */
export const MAYSA_LINE: SpokenLine = {
  id: 'maysa',
  text: 'Ei vielä servulle – aamupala on tuollapäin!',
  duration: 5.2,
  // Not angry, and not asking. He does this every year and he has said it four
  // times already this morning.
  voice: { rate: 1.06, pitch: 0.88, volume: 0.95 },
  cue: 'shout-maysa',
};

/** Presentation only: Mäysä sends Kimmo onwards as the breakfast plate fills. */
export const MAYSA_DONE_LINE: SpokenLine = {
  id: 'maysa-servulle',
  text: 'Noniin, sitten vaan servulle!',
  duration: 5.3,
  voice: MAYSA_LINE.voice,
  cue: 'shout-maysa',
};

/**
 * The porukka out on the juhlasali's floor, the moment Kimmo comes through the
 * hall's doorway.
 *
 * It does the same job Mäysä's line does - it is the only thing that explains
 * why there is a wall of people across a runway - and it is triggered
 * differently for a reason that is the mirror image of his. Mäysä is a man in
 * a doorway you arrive at, so his line waits until the door has actually been
 * tried. These are seven people six metres inside the room, with the bar they
 * are sending him to between him and them: the empties are stacked on the
 * little table at the end of the baari and the hanat are on the baari itself,
 * both of them on the way. A line that waited until the crowd had been run
 * into would be a line that arrived after the runner had already gone past
 * everything it asks for.
 *
 * So it goes off on the way *in*, on `LAN_ROOM_ENTRY` - the split that stamps
 * as the runner clears the doorway. Off the checkpoint rather than off a
 * circle round the crowd, and off the checkpoint rather than off coordinates
 * typed out here: the geometry is level data, and a second copy of it in the
 * script is a copy that goes stale the first time somebody moves the door -
 * or the crowd, which has happened once already.
 *
 * A shout across a room, and not a `distant` one. `distant` is for the yell
 * out of the pirtti, which comes from another building; this is somebody in
 * the same room as you, being funny at your expense.
 */
export const PORUKKA_LINE: SpokenLine = {
  id: 'porukka',
  text: 'Et kai Kimmo sä nyt selvinpäin tullut pelaamaan? Otahan yks sima eka.',
  duration: 4.8,
  // Amused, loud, and pleased with itself. Nobody here is angry - this is the
  // one obstacle in the game that thinks it is doing you a favour.
  voice: { rate: 1.12, pitch: 0.96, volume: 0.95 },
  cue: 'shout-porukka',
};

/** Subtitle for the recorded reaction, shown until the tent replies. */
export const KIMMO_OHO_TEXT = 'Oho!';

/**
 * Everybody in front of the XLMOTO-teltta, the moment the yard's power goes.
 *
 * The one line in the game that is not about where Kimmo is. Mäysä, the cook
 * and the porukka all speak because he arrived somewhere; this one speaks
 * because of something he *did*. The bemari's cable comes off the tolppa, the
 * set stops mid-bar a dozen metres behind him, and the yard it was playing to
 * finds out why at the same moment the juhlanavetta does.
 *
 * That makes it the only consequence in the run anybody comments on out loud,
 * and it is the reason it is worth having at all. The blackout is otherwise a
 * thing that happens to the *scenery*: screens go off in a room you have not
 * reached yet, and the man who caused it is already past it and facing the
 * other way. A yell out of the dark behind him is what turns twenty metres of
 * delayed punishment into something he is told about on the step he earns it.
 *
 * NOT `distant`, though it is shouted across a yard, and that is a decision
 * rather than an oversight. `distant` is the intro yell - one man, inside
 * another building, rendered smaller because that is what the distance is. The
 * whole party is outdoors with nothing between it and the runner, and it is
 * eleven people rather than one. So it gets what the porukka's line gets from
 * the far end of a sixteen-metre hall, which is full weight.
 *
 * *When* it is said is not in here, for the same reason none of the other
 * three carry their own geometry: the trigger is the cable, the cable is level
 * data, and a copy of either in the script is a copy that goes stale. `Game`
 * fires it a beat behind the blackout - see `BLACKOUT_SHOUT_DELAY` for why it
 * is a beat and not the same step.
 */
export const TELTTA_LINE: SpokenLine = {
  id: 'teltta',
  text: 'Voi vittu, "Kimmo"!',
  // Short, because it is shouted at the back of a man who is not stopping. It
  // is off the screen about the time he reaches the palju, and by then there
  // is a hand coming out of the water to look at instead.
  duration: 1.4,
  // The only angry line in the game. The cook is pleased, Mäysä is bored and
  // the porukka thinks it is being funny; these ones just lost the music.
  voice: { rate: 1.22, pitch: 1.04, volume: 1 },
  cue: 'shout-teltta',
};

/**
 * Kempf, standing at the sauna door in the juhlasali's far corner, the moment
 * somebody tries the handle.
 *
 * The only line in the game that answers a *press*. Mäysä and the porukka
 * speak because Kimmo arrived somewhere, the cook because he came into a yard,
 * the teltta because a cable came out of a tolppa; this one is a man replying
 * to a question that was asked by rattling a door two feet from his elbow. So
 * it is triggered off `StepEvents.interacted` in `Game` and off nothing else -
 * there is no circle round him, because standing near a locked door is not
 * asking anybody anything.
 *
 * Which is also why he is allowed to repeat himself, and is the only one who
 * is. The other four get a flag and one line a run, because being told a thing
 * happens once; this is an answer, and a man who answered the first press and
 * then let the next four go by in silence would read as a bug rather than as a
 * man. Try it again and he says it again.
 *
 * He has just lit the sauna stove, so there is no reason to go in yet.
 * The subtitle stays up for the full recording in samples/voice/kempf.m4a.
 */
export const KEMPF_LINE: SpokenLine = {
  id: 'kempf',
  text: 'Joo, mä sain just tulet saunan pesään – sinne ei tarvii ihan heti mennä...',
  duration: 4.2,
  // A quiet reply from a metre away. These settings also keep the speech
  // synthesis fallback calm if the recording cannot be loaded.
  voice: { rate: 0.94, pitch: 0.9, volume: 0.8 },
  cue: 'shout-kempf',
};

/**
 * Every id that can be spoken out loud. A recorded line is matched to its cue
 * by filename, so this is also the list of legal names under samples/voice.
 */
export const SPOKEN_LINE_IDS: readonly string[] = [
  ...RUN_START_LINES.map((line) => line.id),
  HANNU_LINE_ID,
  KOKKI_LINE.id,
  MAYSA_LINE.id,
  MAYSA_DONE_LINE.id,
  PORUKKA_LINE.id,
  TELTTA_LINE.id,
  KEMPF_LINE.id,
];

/** The active line at `t`, or null. Later lines win if two overlap. */
export function activeLine(
  lines: readonly ScriptedLine[],
  t: number,
): ScriptedLine | null {
  let active: ScriptedLine | null = null;
  for (const line of lines) {
    if (t >= line.startTime && t < line.startTime + line.duration) active = line;
  }
  return active;
}
